import React, { useRef, useState, useEffect, useMemo, useCallback } from 'react';
import classnames from 'classnames';
import { DEVICE_TYPE, useDeviceType, useWindowSize } from '@libs/common-next';
import { Button } from '@petsmart-ui/sparky';

import './HorizontalCarousel.scss';

const defaultBreakpoints: Record<number, { perView: number; gap: number }> = {
    1200: {
        perView: 6,
        gap: 64,
    },
    1080: {
        perView: 5,
        gap: 64,
    },
    960: {
        perView: 5,
        gap: 64,
    },
    768: {
        perView: 4,
        gap: 16,
    },
    560: {
        perView: 3,
        gap: 16,
    },
    375: {
        perView: 3,
        gap: 16,
    },
};

type CarouselProps = {
    className: string;
    children: React.ReactElement[];
    onLeftArrowClick: () => void;
    onRightArrowClick: () => void;
    onDragging: (x: boolean) => void;
    perViewDefault: number;
    breakpoints?: Record<number, { perView: number; gap: number }>;
};

export function HorizontalCarousel({
    children,
    className,
    perViewDefault = 6,
    onLeftArrowClick,
    onRightArrowClick,
    breakpoints = defaultBreakpoints,
    onDragging,
}: CarouselProps) {
    const carouselRef = useRef<HTMLDivElement>(null);
    const [isMouseDown, setIsMouseDown] = useState(false);
    const [startX, setStartX] = useState(0);
    const [currentTranslate, setCurrentTranslate] = useState(0);
    const [prevTranslate, setPrevTranslate] = useState(0);
    const [showNext, setShowNext] = useState(false);
    const [showPrev, setShowPrev] = useState(false);
    const [breakpoint, setBreakpoint] = useState({
        perView: perViewDefault,
        gap: 64,
    });

    const width = useWindowSize();
    const deviceType = useDeviceType();
    const isSmallScreen = deviceType === DEVICE_TYPE.MOBILE;

    const updateBreakpoints = useCallback(() => {
        let match = false;
        if (window.matchMedia) {
            for (const point in breakpoints) {
                if (window.matchMedia(`(max-width: ${point}px)`).matches) {
                    const newBreakpoint = breakpoints[point];
                    if (newBreakpoint.perView !== breakpoint.perView) {
                        setBreakpoint(newBreakpoint);
                    }
                    match = true;
                }
            }
        }
        if (!match) {
            setBreakpoint({ perView: perViewDefault, gap: 64 });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- prevent re-render
    }, [width]);

    const updateButtonVisibility = useCallback(() => {
        if (carouselRef?.current) {
            const { scrollWidth, clientWidth } = carouselRef.current;
            setShowNext(scrollWidth - clientWidth - -currentTranslate > 0);
            setShowPrev(currentTranslate < 0);
        }
    }, [currentTranslate]);

    const itemWidth = useMemo(() => {
        if (carouselRef?.current) {
            const { clientWidth } = carouselRef.current;

            if (clientWidth && breakpoint?.perView) {
                const childWidth = clientWidth / breakpoint.perView;
                return { minWidth: `${childWidth}px` };
            }
        }
        return {};
        // eslint-disable-next-line react-hooks/exhaustive-deps -- need window width dependency to update item width
    }, [width]);

    useEffect(() => {
        updateButtonVisibility();
    }, [updateButtonVisibility, prevTranslate, carouselRef?.current?.scrollWidth]);

    useEffect(() => {
        updateBreakpoints();
    }, [updateBreakpoints]);

    const handleMouseDown = (e: React.MouseEvent<HTMLImageElement>) => {
        e.preventDefault();
        setIsMouseDown(true);
        setStartX(e.pageX);
    };

    const handleMouseUp = () => {
        if (carouselRef.current) {
            setIsMouseDown(false);
            let calcTranslate = currentTranslate;

            const { scrollWidth, clientWidth } = carouselRef.current;
            if (scrollWidth - clientWidth - -currentTranslate < 0) {
                calcTranslate = clientWidth - scrollWidth;
                setCurrentTranslate(clientWidth - scrollWidth);
            }

            if (currentTranslate === prevTranslate) {
                onDragging(false);
            }

            if (currentTranslate > 0) {
                calcTranslate = 0;
                setCurrentTranslate(0);
            }
            setPrevTranslate(calcTranslate);
        }
    };

    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
        if (isMouseDown) {
            const x = e.pageX;
            const walk = x - startX;
            if (Math.abs(e.clientX - startX) > 10) {
                onDragging(true);
                setCurrentTranslate(prevTranslate + walk);
            }
        }
    };

    const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
        setIsMouseDown(true);
        onDragging(true);
        setStartX(e.touches[0].pageX);
    };

    const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
        if (isMouseDown) {
            const x = e.touches[0].pageX;
            const walk = x - startX;
            setCurrentTranslate(prevTranslate + walk);
        }
    };

    const handleEndEvent = () => {
        if (isMouseDown) {
            handleMouseUp();
        }
    };

    const handleNext = () => {
        if (carouselRef.current) {
            const { scrollWidth, clientWidth } = carouselRef.current;
            setCurrentTranslate(clientWidth - scrollWidth);
            setPrevTranslate(clientWidth - scrollWidth);
            onRightArrowClick();
        }
    };

    const handlePrev = () => {
        setCurrentTranslate(0);
        setPrevTranslate(0);
        onLeftArrowClick();
    };

    const componentClassName = classnames('carousel-container', className);

    return (
        <div aria-label="Image carousel" className={componentClassName}>
            {isSmallScreen ? (
                <div className="carousel-mobile" role="listbox">
                    {React.Children.map(children, (child, index) => (
                        <div className="carousel-outer-item" key={child?.key || index}>
                            {child}
                        </div>
                    ))}
                </div>
            ) : (
                <>
                    <div
                        id="swipe-carousel"
                        role="listbox"
                        className="carousel"
                        tabIndex={0}
                        ref={carouselRef}
                        onMouseDown={handleMouseDown}
                        onMouseLeave={handleEndEvent}
                        onMouseUp={handleMouseUp}
                        onMouseMove={handleMouseMove}
                        onTouchStart={handleTouchStart}
                        onTouchEnd={handleEndEvent}
                        onTouchMove={handleTouchMove}
                        style={{
                            transform: `translate3d(${currentTranslate}px, 0, 0)`,
                            transition: isMouseDown ? 'none' : 'transform 800ms cubic-bezier(0.165, 0.84, 0.44, 1)', // Smooth transition when not dragging
                        }}
                    >
                        <div className="carousel-inner">
                            {React.Children.map(children, (child, index) => (
                                <div
                                    className="carousel-outer-item"
                                    style={{
                                        gap: `${breakpoint?.gap}px`,
                                        ...itemWidth,
                                    }}
                                    key={child?.key || index}
                                >
                                    {child}
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="carousel-arrow-container">
                        {showPrev && (
                            <Button
                                circle
                                text="arrow-prev"
                                aria-label="Previous slide"
                                iconName="chevron-left"
                                iconPosition="before"
                                iconSize="md"
                                hideText
                                variant="primary"
                                className="carousel-arrows carousel-arrow-left"
                                data-testid="carousel-arrow-left"
                                onClick={handlePrev}
                            />
                        )}
                        {showNext && (
                            <Button
                                circle
                                text="arrow-next"
                                aria-label="Next slide"
                                iconName="chevron-right"
                                iconPosition="before"
                                iconSize="md"
                                hideText
                                variant="primary"
                                className="carousel-arrows carousel-arrow-right"
                                data-testid="carousel-arrow-right"
                                onClick={handleNext}
                            />
                        )}
                    </div>
                </>
            )}
        </div>
    );
}
